import { Modal } from '@/components/admin/Modal';
import { Button } from '@/components/ui/button';
import { ApiError, api } from '@/lib/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

/**
 * 관리자 첨부파일 설정 hub. 두 섹션:
 *  1) 문서 처리 설정 — 분류/해석 LLM 엔드포인트, 사용자당 동시 변환 상한, 분석 프롬프트.
 *  2) 채팅별 첨부 관리 — 세션 단위로 그룹화된 첨부 파일 (진행 중 중지 / 삭제).
 */
export function DocumentSettingsModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const settingsQuery = useQuery({
    queryKey: ['admin', 'document-settings'],
    queryFn: api.adminGetDocumentSettings,
  });
  const endpointsQuery = useQuery({
    queryKey: ['admin', 'llm-endpoints'],
    queryFn: api.adminLlmList,
  });

  const [endpointId, setEndpointId] = useState<string | null>(null);
  const [maxPerUser, setMaxPerUser] = useState(3);
  const [prompt, setPrompt] = useState('');
  const [imageMaxPerUser, setImageMaxPerUser] = useState(3);
  const [imageOcrPrompt, setImageOcrPrompt] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (settingsQuery.data) {
      setEndpointId(settingsQuery.data.documentLlmEndpointId);
      setMaxPerUser(settingsQuery.data.documentIngestMaxPerUser);
      setPrompt(settingsQuery.data.documentAnalyzePrompt ?? '');
      setImageMaxPerUser(settingsQuery.data.imageIngestMaxPerUser);
      setImageOcrPrompt(settingsQuery.data.imageOcrPrompt ?? '');
    }
  }, [settingsQuery.data]);

  const saveMut = useMutation({
    mutationFn: api.adminUpdateDocumentSettings,
    onSuccess: () => {
      setSaveError(null);
      qc.invalidateQueries({ queryKey: ['admin', 'document-settings'] });
    },
    onError: (e) => setSaveError(e instanceof ApiError ? e.message : '저장 실패'),
  });

  const onSave = () => {
    saveMut.mutate({
      documentLlmEndpointId: endpointId,
      documentIngestMaxPerUser: maxPerUser,
      documentAnalyzePrompt: prompt.trim() || null,
      imageIngestMaxPerUser: imageMaxPerUser,
      imageOcrPrompt: imageOcrPrompt.trim() || null,
    });
  };

  const visionConfigured = settingsQuery.data?.visionDefaultConfigured ?? false;

  const endpoints = endpointsQuery.data?.endpoints ?? [];

  return (
    <Modal
      open
      onClose={onClose}
      title="첨부파일 설정"
      description="사용자 문서를 분류·해석할 LLM 엔드포인트와 동시 변환 한도를 설정하고, 채팅별 첨부를 관리합니다."
      size="xl"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            닫기
          </Button>
          <Button onClick={onSave} disabled={saveMut.isPending}>
            {saveMut.isPending ? '저장 중…' : '설정 저장'}
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        <section className="space-y-3">
          <h3 className="text-sm font-semibold">문서 처리 설정</h3>
          {saveError ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {saveError}
            </div>
          ) : null}
          <label className="flex flex-col gap-1">
            <span className="text-xs text-foreground-muted">
              분류·해석 LLM 엔드포인트 (미선택 시 시스템 기본 엔드포인트, 없으면 LLM 분석 생략)
            </span>
            <select
              value={endpointId ?? ''}
              onChange={(e) => setEndpointId(e.target.value || null)}
              className="h-9 rounded-md border border-border bg-surface px-2 text-sm"
            >
              <option value="">(시스템 기본 사용)</option>
              {endpoints.map((ep) => (
                <option key={ep.id} value={ep.id}>
                  {ep.displayName ?? ep.name} — {ep.model}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-foreground-muted">
              사용자당 동시 변환 상한 (초과 시 대기)
            </span>
            <input
              type="number"
              min={1}
              max={20}
              value={maxPerUser}
              onChange={(e) =>
                setMaxPerUser(Math.max(1, Math.min(20, Number(e.target.value) || 1)))
              }
              className="h-9 w-24 rounded-md border border-border bg-surface px-2 text-sm"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-foreground-muted">
              분류·해석 프롬프트 (비우면 내장 기본값 사용)
            </span>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={4}
              placeholder="(내장 기본 프롬프트 사용)"
              className="rounded-md border border-border bg-surface px-2 py-1.5 text-sm font-mono"
            />
          </label>
        </section>

        <section className="space-y-3">
          <h3 className="text-sm font-semibold">이미지 처리 설정</h3>
          {visionConfigured ? (
            <p className="text-xs text-foreground-muted">
              이미지는 업로드 시 전역 비전 기본 모델로 OCR·캡션을 추출해 저장합니다. 시각적 질문은
              대화 중 이미지를 다시 읽어 답합니다.
            </p>
          ) : (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              전역 비전 기본 모델이 설정되어 있지 않아 이미지 첨부 기능이 비활성화되어 있습니다. LLM
              엔드포인트 설정에서 비전 지원 모델을 기본으로 지정하세요.
            </div>
          )}
          <label className="flex flex-col gap-1">
            <span className="text-xs text-foreground-muted">
              사용자당 동시 이미지 처리 상한 (초과 시 대기)
            </span>
            <input
              type="number"
              min={1}
              max={20}
              value={imageMaxPerUser}
              onChange={(e) =>
                setImageMaxPerUser(Math.max(1, Math.min(20, Number(e.target.value) || 1)))
              }
              className="h-9 w-24 rounded-md border border-border bg-surface px-2 text-sm"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-foreground-muted">
              이미지 OCR(텍스트 추출) 프롬프트 (비우면 내장 기본값 사용)
            </span>
            <textarea
              value={imageOcrPrompt}
              onChange={(e) => setImageOcrPrompt(e.target.value)}
              rows={4}
              placeholder="(내장 기본 프롬프트 사용)"
              className="rounded-md border border-border bg-surface px-2 py-1.5 text-sm font-mono"
            />
          </label>
        </section>

        <section className="space-y-2">
          <h3 className="text-sm font-semibold">채팅별 첨부 관리</h3>
          <AttachmentsBySession />
        </section>
      </div>
    </Modal>
  );
}

function AttachmentsBySession() {
  const qc = useQueryClient();
  const [ownerEmail, setOwnerEmail] = useState('');
  const query = useQuery({
    queryKey: ['admin', 'attachments-by-session', ownerEmail],
    queryFn: () =>
      api.adminAttachmentsBySession({ ownerEmail: ownerEmail || undefined, limit: 20 }),
  });

  const cancelMut = useMutation({
    mutationFn: (id: string) =>
      api.adminCancelFileUpload(id, { reason: '대용량 파일 사유로 중단되었습니다.' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'attachments-by-session'] }),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => api.adminDeleteFileUpload(id, { confirmActive: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'attachments-by-session'] }),
  });

  const groups = query.data?.groups ?? [];
  const inProgress = (s: string) =>
    s === 'uploading' || s === 'uploaded' || s === 'queued' || s === 'processing';

  return (
    <div className="space-y-2">
      <input
        type="text"
        value={ownerEmail}
        onChange={(e) => setOwnerEmail(e.target.value)}
        placeholder="소유자 이메일로 필터 (substring)"
        className="h-9 w-72 rounded-md border border-border bg-surface px-2 text-sm"
      />
      <div className="max-h-72 overflow-y-auto rounded-md border border-border divide-y divide-border">
        {query.isLoading ? (
          <div className="px-3 py-6 text-center text-sm text-foreground-muted">불러오는 중…</div>
        ) : groups.length === 0 ? (
          <div className="px-3 py-6 text-center text-sm text-foreground-muted">
            첨부가 있는 대화가 없습니다.
          </div>
        ) : (
          groups.map((g) => (
            <div key={g.sessionId ?? Math.random()} className="px-3 py-2">
              <div className="text-sm font-medium truncate">
                {g.sessionTitle ?? '(제목 없음)'}
                <span className="ml-2 text-xs text-foreground-muted">
                  {g.ownerDisplayName ?? g.ownerEmail ?? '—'}
                </span>
              </div>
              <ul className="mt-1 space-y-1">
                {g.files.map((f) => (
                  <li key={f.id} className="flex items-center gap-2 text-xs">
                    <span className="truncate flex-1" title={f.originalName}>
                      {f.originalName}
                    </span>
                    <span className="font-mono uppercase text-foreground-muted">{f.kind}</span>
                    <span
                      className={
                        f.status === 'failed' || f.status === 'canceled'
                          ? 'text-destructive'
                          : f.status === 'ready'
                            ? 'text-success'
                            : 'text-foreground-muted'
                      }
                    >
                      {f.status}
                    </span>
                    {inProgress(f.status) ? (
                      <button
                        type="button"
                        onClick={() => cancelMut.mutate(f.id)}
                        className="text-foreground-muted hover:text-destructive"
                      >
                        중지
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm(`"${f.originalName}" 파일을 삭제할까요?`))
                          deleteMut.mutate(f.id);
                      }}
                      className="text-foreground-muted hover:text-destructive"
                    >
                      삭제
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
